import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL, API_ENDPOINTS } from '../config/api';
import { getUserInfo, isAuthenticated, getAuthHeaders } from '../utils/auth';
import { loadRazorpay, getPaymentConfig, createPaymentOrder, reportPaymentFailure } from '../services/payments';
import { clearCreditsCache } from '../services/credits';
import './BuyCredits.css';

export default function BuyCredits() {
  const navigate = useNavigate();
  const USD_CONVERSION_RATE = 83; // Fallback USD/INR rate when live fetch fails
  const GST_RATE = 0.18; // 18% GST
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [currentCredits, setCurrentCredits] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('INR');
  const [usdInrRate, setUsdInrRate] = useState(USD_CONVERSION_RATE);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [discountedPlans, setDiscountedPlans] = useState([]);
  const [referralCode, setReferralCode] = useState(localStorage.getItem('referralCode') || '');
  const [referralValidated, setReferralValidated] = useState(false);
  const [referralError, setReferralError] = useState(null);

  const normalizePriceNumber = (value) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(num) ? num : 0;
  };

  // Fetch live USD/INR rate to avoid stale conversions; fallback stays in place if it fails.
  useEffect(() => {
    let cancelled = false;
    const fetchUsdRate = async () => {
      try {
        const res = await fetch('https://api.exchangerate-api.com/v4/latest/INR');
        if (!res.ok) throw new Error(`Rate fetch failed (${res.status})`);
        const data = await res.json();
        const usdPerInr = Number(data?.rates?.USD); // example: 0.012 for 1 INR
        const inrPerUsd = usdPerInr > 0 ? Number((1 / usdPerInr).toFixed(4)) : null;
        if (!cancelled && Number.isFinite(inrPerUsd) && inrPerUsd > 0) {
          setUsdInrRate(inrPerUsd);
        }
      } catch (err) {
        console.warn('Using fallback USD/INR rate; live fetch failed', err);
      }
    };
    fetchUsdRate();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = getUserInfo();
        
        // If not logged in, redirect to signup
        if (!isAuthenticated() || !user) {
          navigate('/signup');
          return;
        }

        const headers = getAuthHeaders();

        const res = await fetch(`${BACKEND_URL}/api/plans/all`, {
          credentials: 'include',
          headers
        });
        if (!res.ok) throw new Error(`Failed to load plans (${res.status})`);
        const data = await res.json();
        const rawPlans = Array.isArray(data) ? data : (Array.isArray(data.plans) ? data.plans : []);
        const normalized = rawPlans.map((p) => {
          const priceInr = normalizePriceNumber(p.price ?? p.priceInr ?? p.inrPrice);
          const priceUsdRaw = normalizePriceNumber(p.priceUsd ?? p.priceUSD ?? p.usdPrice);
          const priceUsd = priceUsdRaw || (priceInr ? Number((priceInr / usdInrRate).toFixed(2)) : 0);
          return {
            ...p,
            id: p.id || p._id || p.planId || p.name,
            credits: Number(p.credits) || 0,
            priceInr,
            priceUsd,
          };
        });
        setPlans(normalized);
        if (!selectedPlanId && normalized.length) {
          setSelectedPlanId(normalized[0].id);
        }
      } catch (err) {
        console.error('Error fetching plans', err);
        setPlans([]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [usdInrRate]);

  const refreshCredits = useCallback(async () => {
    try {
      console.log('🔄 [Credits] Clearing cache and refreshing credits...');
      
      // Clear cache to force fresh fetch
      clearCreditsCache();
      
      if (!isAuthenticated()) {
        console.warn('No auth available for credit refresh');
        return null;
      }
      
      const headers = getAuthHeaders();
      
      const res = await fetch(API_ENDPOINTS.USERS.CREDITS, {
        headers,
        credentials: 'include',
      });
      
      if (!res.ok) {
        console.error('Failed to fetch credits:', res.status);
        return null;
      }
      
      const data = await res.json();
      const total = data.totalCredits ?? null;
      setCurrentCredits(total);
      
      console.log('✅ [Credits] Refreshed:', total);
      
      // Dispatch event to update header and other components
      window.dispatchEvent(new CustomEvent('creditsUpdated', { detail: { totalCredits: total } }));
      
      return total;
    } catch (err) {
      console.warn('Failed to refresh credits after payment', err);
      return null;
    }
  }, []);

  const validateCoupon = useCallback(async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponValidating(true);
    setCouponError(null);
    
    try {
      if (!isAuthenticated()) {
        navigate('/signup');
        return;
      }

      // Prepare items for validation with currency
      const items = plans.map(plan => ({
        purchaseAmount: displayCurrency === 'USD' ? plan.priceUsd : plan.priceInr,
        planId: plan.id,
        currency: displayCurrency
      }));

      const headers = getAuthHeaders();

      const response = await fetch(API_ENDPOINTS.COUPONS.VALIDATE, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          couponCode: couponCode.trim(),
          items
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Invalid coupon code');
      }

      const data = await response.json();
      
      if (data.valid) {
        setCouponApplied(true);
        setDiscountedPlans(data.items || []);
        setCouponError(null);
      } else {
        setCouponError(data.message || 'Coupon is not valid');
        setCouponApplied(false);
        setDiscountedPlans([]);
      }
    } catch (err) {
      console.error('Coupon validation failed', err);
      setCouponError(err.message || 'Failed to validate coupon');
      setCouponApplied(false);
      setDiscountedPlans([]);
    } finally {
      setCouponValidating(false);
    }
  }, [couponCode, plans, navigate, displayCurrency]);

  const removeCoupon = useCallback(() => {
    setCouponCode('');
    setCouponApplied(false);
    setCouponError(null);
    setDiscountedPlans([]);
  }, []);

  // Re-validate coupon when currency changes
  useEffect(() => {
    if (couponApplied && couponCode.trim()) {
      // Re-validate with new currency
      validateCoupon();
    }
  }, [displayCurrency]);

  const getDiscountedPrice = useCallback((planId, currency) => {
    if (!couponApplied || !discountedPlans.length) return null;
    const discounted = discountedPlans.find(item => item.planId === planId);
    return discounted ? { finalAmount: discounted.finalAmount, currency: displayCurrency } : null;
  }, [couponApplied, discountedPlans, displayCurrency]);

  const startCheckout = useCallback(async (plan) => {
    try {
      setPaymentError(null);
      setPaymentMessage(null);
      setPayingPlanId(plan.id || plan._id || plan.name);

      const user = getUserInfo();
      if (!isAuthenticated() || !user) {
        navigate('/signup');
        return;
      }
      
      // Fire Google Ads conversion event if this is a new user from ads
      const isNewUser = localStorage.getItem('is_new_user') === 'true';
      const userFromAds = localStorage.getItem('user_from_ads') === 'true';
      
      if (isNewUser && userFromAds) {
        console.log('🎯 Firing Google Ads conversion event for new user from ads');
        
        // Push to dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'get_credits_click',
          'user_type': 'new',
          'from_ads': true,
          'plan_name': plan.name,
          'plan_credits': plan.credits
        });
        
        // Clear the flags so we don't fire again
        localStorage.removeItem('is_new_user');
      }

      await loadRazorpay();

      const config = await getPaymentConfig();
      const preferredCurrency = displayCurrency === 'USD' ? 'USD' : 'INR';
      const rawPlanId = plan.id || plan._id || plan.planId || plan.name;
      const planId = Number.isFinite(Number(rawPlanId)) ? Number(rawPlanId) : rawPlanId;
      
      // Check if coupon is applied and get discounted price
      const discountedInfo = getDiscountedPrice(planId, preferredCurrency);
      const currency = discountedInfo ? discountedInfo.currency : (plan.currency || config.currency || preferredCurrency);
      const amountBaseBeforeGst = discountedInfo ? discountedInfo.finalAmount : (currency === 'USD'
        ? (plan.priceUsd || (plan.priceInr ? plan.priceInr / usdInrRate : 0))
        : (plan.priceInr || 0));
      // Add 18% GST to the base amount
      const amountBase = amountBaseBeforeGst * (1 + GST_RATE);
      const amountMinorUnits = Math.round((Number(amountBase) || 0) * 100);

      if (!amountMinorUnits || Number.isNaN(amountMinorUnits)) {
        setPaymentError('Invalid plan amount. Please try another plan or contact support.');
        setPayingPlanId(null);
        return;
      }

      const normalizedUserId = Number.isFinite(Number(user.userId || user.id))
        ? Number(user.userId || user.id)
        : (user.userId || user.id || user.email);

      // Log coupon info
      console.log('💳 [Payment] Creating order with coupon details:', {
        couponCode: couponApplied ? couponCode : 'none',
        couponApplied,
        discountApplied: discountedInfo ? 'yes' : 'no',
      });

      const order = await createPaymentOrder({
        userId: normalizedUserId,
        planId: planId, // Add planId at top level for backend
        amount: amountBase,
        currency,
        receipt: `receipt_order_${rawPlanId}_${Date.now()}`,
        notes: {
          planName: String(plan.name || ''),
          planId: String(planId || ''),
          credits: String(plan.credits || ''),
          price: String(amountBase),
          priceBeforeGst: String(amountBaseBeforeGst),
          gstAmount: String(amountBaseBeforeGst * GST_RATE),
          gstRate: '18%',
          originalPrice: discountedInfo ? String(currency === 'USD' ? plan.priceUsd : plan.priceInr) : String(amountBaseBeforeGst),
          discountApplied: discountedInfo ? 'true' : 'false',
          currency,
          // Include coupon details if applied
          couponCode: couponApplied ? String(couponCode) : '',
          couponApplied: couponApplied ? 'true' : 'false',
          discountPercentage: discountedInfo ? String(discountedInfo.discountPercentage || '') : '',
          // Include referral code if provided
          referralCode: referralCode.trim() || '',
        },
      });

      const orderAmountRaw = Number(order.amount);
      const amountForCheckout = Number.isFinite(orderAmountRaw) && orderAmountRaw > 0
        ? orderAmountRaw
        : amountMinorUnits;

      const options = {
        key: order.keyId || config.keyId,
        amount: amountForCheckout,
        currency: order.currency || currency,
        name: 'BlackKite AI',
        description: `${plan.name} Plan Purchase`,
        order_id: order.orderId || order.id,
        handler: async (response) => {
          try {
            console.log('💳 [Payment] Razorpay handler called with response:', {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id
            });
            
            const updated = await refreshCredits();
            
            // Redirect to payment success page for Google Ads conversion tracking
            const successUrl = `/payment-success?credits=${encodeURIComponent(updated || plan.credits || 'N/A')}&amount=${encodeURIComponent(formatPrice(amountBase / 100, currency))}&orderId=${encodeURIComponent(response.razorpay_order_id || '')}&paymentId=${encodeURIComponent(response.razorpay_payment_id || '')}`;
            
            window.location.href = successUrl;
          } catch (err) {
            console.error('❌ [Payment] Error:', err.message);
            setPaymentError('Payment processing failed. Please contact support if you were charged.');
            setPayingPlanId(null);
          }
        },
        modal: {
          ondismiss: async () => {
            try {
              await reportPaymentFailure({
                razorpayOrderId: order.orderId || order.id,
                errorCode: 'USER_CANCELLED',
                errorDescription: 'User closed payment popup',
              });
            } catch (err) {
              console.warn('Failed to report payment dismissal', err);
            } finally {
              setPayingPlanId(null);
            }
          },
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
          contact: user.phone || user.contact || '',
        },
        theme: {
          color: '#0A2540',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Payment start failed', err);
      setPaymentError(err.message || 'Unable to start payment. Please try again.');
      setPayingPlanId(null);
    }
  }, [navigate, displayCurrency, usdInrRate, getDiscountedPrice]);

  const featuredPlanName = useMemo(() => {
    if (!plans.length) return null;
    const sorted = [...plans].sort((a, b) => (b.credits || 0) - (a.credits || 0));
    return sorted[0]?.name || null;
  }, [plans]);

  const formatPrice = (value, currency) => {
    if (typeof value !== 'number') return value || '';
    if (currency === 'USD') {
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const getPlanIcon = (name = '') => {
    const normalized = name.toLowerCase();
    if (normalized.includes('ultimate')) return '👑';
    if (normalized.includes('premium')) return '💎';
    if (normalized.includes('standard')) return '🚀';
    return '🌱';
  };

  const formatCreditsDisplay = (credits) => {
    const c = Number(credits) || 0;
    if (c === 15) {
      return { base: 12, bonus: 3, total: 15, display: '12 + 3 Free' };
    } else if (c === 8) {
      return { base: 6, bonus: 2, total: 8, display: '6 + 2 Free' };
    }
    return { base: c, bonus: 0, total: c, display: `${c}` };
  };

  const handleGetCredits = (planName) => {
    // TODO: Implement payment gateway integration
      // console.log('Get credits for plan:', planName);
    alert(`Payment integration coming soon for ${planName} plan!`);
  };

  return (
    <div className="buy-credits-container">
      <div className="buy-credits-header">
        <span style={{ fontSize: '2rem' }}>💬</span>
        <h1>Buy Credits</h1>
        <span className="buy-credits-subtitle">You can split credits into 30-minute sessions. (Plans auto-update from server)</span>
      </div>

      <div className="currency-toggle">
        <div className="currency-toggle-buttons">
          <button
            type="button"
            className={displayCurrency === 'INR' ? 'active' : ''}
            onClick={() => setDisplayCurrency('INR')}
          >
            ₹ INR
          </button>
          <button
            type="button"
            className={displayCurrency === 'USD' ? 'active' : ''}
            onClick={() => setDisplayCurrency('USD')}
          >
            $ USD
          </button>
        </div>
      </div>

      <div className="coupon-section coupon-section-inline">
        <div className="coupon-input-wrapper">
          <input
            type="text"
            className="coupon-input"
            placeholder="Referral code (optional)"
            value={referralCode}
            onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralValidated(false); setReferralError(null); }}
          />
          <input
            type="text"
            className="coupon-input"
            placeholder="Coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            disabled={couponApplied}
          />
          {!couponApplied ? (
            <button
              type="button"
              className="btn-apply-coupon"
              onClick={validateCoupon}
              disabled={couponValidating || !couponCode.trim()}
            >
              {couponValidating ? 'Validating...' : 'Apply'}
            </button>
          ) : (
            <button
              type="button"
              className="btn-remove-coupon"
              onClick={removeCoupon}
            >
              Remove
            </button>
          )}
        </div>
        {referralError && (
          <div className="coupon-error">
            ⚠️ {referralError}
          </div>
        )}
        {couponError && (
          <div className="coupon-error">
            ⚠️ {couponError}
          </div>
        )}
        {couponApplied && (
          <div className="coupon-success">
            ✓ Coupon applied successfully! Discounts shown below.
          </div>
        )}
      </div>

      <div className="credit-consumption-notice">
        <div className="notice-icon">ℹ️</div>
        <div className="notice-content">
          <strong>Credit Consumption Policy:</strong> Each credit provides 1 hour of interview time. 
          If your interview continues beyond 1 hour, the next credit will be automatically consumed. 
          <span className="highlight-text">Your interview will not disconnect</span> while in progress – enjoy uninterrupted sessions!
        </div>
      </div>

      {paymentMessage && (
        <div className="pricing-success">
          {paymentMessage}
        </div>
      )}

      {paymentError && (
        <div className="pricing-error">
          {paymentError}
        </div>
      )}

      <div className="pricing-cards-container">
        {loading && (
          <div className="pricing-card skeleton">
            <div className="skeleton-block" style={{ height: 18, width: '50%' }} />
            <div className="skeleton-block" style={{ height: 42, width: '80%' }} />
            <div className="skeleton-block" style={{ height: 24, width: '60%' }} />
            <div className="skeleton-block" style={{ height: 44, width: '100%' }} />
          </div>
        )}

        {!loading && !error && plans.length === 0 && (
          <div className="pricing-error">
            Plans are temporarily unavailable. Please check back shortly.
          </div>
        )}

        {!loading && !error && plans.map((plan, idx) => {
          const displayName = (plan.name || '').trim() || 'Plan';
          const descriptionLabel = (() => {
            const desc = (plan.description || '').trim();
            // Prefer backend description only when it references this plan; otherwise derive from name.
            return desc && desc.toLowerCase().includes(displayName.toLowerCase())
              ? desc
              : `${displayName} plan`;
          })();
          const dots = plan.credits || 1;
          const priceValue = displayCurrency === 'USD' ? plan.priceUsd : plan.priceInr;
          const discountedInfo = getDiscountedPrice(plan.id, displayCurrency);
          const basePriceBeforeGst = discountedInfo ? discountedInfo.finalAmount : priceValue;
          // Display base price with "+ 18% GST" label
          const priceLabel = formatPrice(basePriceBeforeGst, displayCurrency);
          const originalPriceLabel = discountedInfo ? formatPrice(priceValue, displayCurrency) : null;
          const creditsInfo = formatCreditsDisplay(plan.credits || 0);
          const creditsLabel = creditsInfo.bonus > 0 
            ? `${creditsInfo.display} Credits` 
            : `${plan.credits || 0} Credits`;
          const planIcon = getPlanIcon(displayName);
          const isSelected = (selectedPlanId || plans[0]?.id) === plan.id;
          const isUltimate = (Number(plan.credits) === 15) || displayName.toLowerCase().includes('ultimate');
          return (
            <div
              key={`${plan.name}-${idx}`}
              className={`pricing-card${isSelected ? ' selected' : ''}${isUltimate ? ' ultimate' : ''}`}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              <div className="pricing-card-icon">
                {Array.from({ length: dots }).map((_, i) => (
                  <span key={i} className="icon-dot"></span>
                ))}
              </div>

              <h2 className="pricing-card-title">{displayName}</h2>

              <div className="plan-icon" aria-hidden="true">{planIcon}</div>

              <div className="pricing-card-price">
                {originalPriceLabel && (
                  <span className="price-original">{originalPriceLabel}</span>
                )}
                <span className="price-amount">{priceLabel}</span>
                <span className="gst-label">+ 18% GST</span>
                {originalPriceLabel && (
                  <span className="discount-badge">Discounted!</span>
                )}
              </div>

              <div className="pricing-card-credits">
                {creditsInfo.bonus > 0 ? (
                  <strong>
                    <span className="credits-base">{creditsInfo.base}</span>
                    <span className="credits-plus"> + </span>
                    <span className="credits-bonus">{creditsInfo.bonus} Free</span>
                    <span className="credits-text"> Credits</span>
                  </strong>
                ) : (
                  <strong>{creditsLabel}</strong>
                )}
                <div className="credits-minutes">{creditsInfo.total * 60} minutes</div>
                <div className="plan-description">{descriptionLabel}</div>
              </div>

              <div className="refund-policy-notice">
                <p>
                  Please read our <Link to="/refund-policy" className="refund-policy-link">Refund Policy</Link> before purchasing.
                </p>
              </div>

              <button
                className="btn-get-credits"
                onClick={() => startCheckout(plan)}
                disabled={payingPlanId === (plan.id || plan._id || plan.name)}
              >
                {payingPlanId === (plan.id || plan._id || plan.name) ? 'Processing...' : 'Get Credits'}
                <span style={{ fontSize: '1.2rem', marginLeft: 6 }}>→</span>
              </button>
            </div>
          );
        })}

        {/* Monthly Subscription Card */}
        {!loading && !error && (
          <div className="pricing-card monthly-subscription">
            <div className="pricing-card-icon">
              {Array.from({ length: 20 }).map((_, i) => (
                <span key={i} className="icon-dot"></span>
              ))}
            </div>

            <h2 className="pricing-card-title">Monthly Subscription</h2>

            <div className="plan-icon" aria-hidden="true">🏆</div>

            <div className="pricing-card-price">
              <span className="price-amount">
                {displayCurrency === 'USD'
                  ? formatPrice(Number((5000 / usdInrRate).toFixed(2)), 'USD')
                  : '₹5,000'}
              </span>
              <span className="gst-label">+ 18% GST</span>
              <span className="subscription-period">/ per month</span>
            </div>

            <div className="pricing-card-credits">
              <strong>
                <span className="credits-bonus">Unlimited</span>
                <span className="credits-text"> Credits</span>
              </strong>
              <div className="credits-minutes">Unlimited minutes</div>
              <div className="plan-description">Monthly Subscription plan</div>
            </div>

            <div className="refund-policy-notice">
              <p>
                Please read our <Link to="/refund-policy" className="refund-policy-link">Refund Policy</Link> before purchasing.
              </p>
            </div>

            <div className="subscription-contact">
              <p className="subscription-contact-title">Connect with our Sales Team</p>
              <div className="subscription-contact-buttons">
                <a
                  href="mailto:support@blackkiteai.com?subject=Monthly%20Subscription%20Inquiry"
                  className="btn-contact-sales email"
                >
                  <span role="img" aria-label="email">📧</span> Email Sales
                </a>
                <a
                  href="https://wa.me/917259730473?text=Hi%2C%20I%27m%20interested%20in%20the%20Monthly%20Subscription%20plan%20(Unlimited%20Credits%20%E2%82%B95%2C000%2Fmonth)"
                  className="btn-contact-sales whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span role="img" aria-label="whatsapp">💬</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Yearly Subscription Card */}
        {!loading && !error && (
          <div className="pricing-card yearly-subscription">
            <div className="pricing-card-icon">
              {Array.from({ length: 20 }).map((_, i) => (
                <span key={i} className="icon-dot"></span>
              ))}
            </div>

            <h2 className="pricing-card-title">Yearly Subscription</h2>

            <div className="plan-icon" aria-hidden="true">🌟</div>

            <div className="pricing-card-price">
              <span className="price-amount">
                {displayCurrency === 'USD'
                  ? formatPrice(Number((16000 / usdInrRate).toFixed(2)), 'USD')
                  : '₹16,000'}
              </span>
              <span className="gst-label">+ 18% GST</span>
              <span className="subscription-period">/ per year</span>
            </div>

            <div className="pricing-card-credits">
              <strong>
                <span className="credits-bonus">Unlimited</span>
                <span className="credits-text"> Credits</span>
              </strong>
              <div className="credits-minutes">Unlimited minutes</div>
              <div className="plan-description">Yearly Subscription plan</div>
            </div>

            <div className="refund-policy-notice">
              <p>
                Please read our <Link to="/refund-policy" className="refund-policy-link">Refund Policy</Link> before purchasing.
              </p>
            </div>

            <div className="subscription-contact">
              <p className="subscription-contact-title">Connect with our Sales Team</p>
              <div className="subscription-contact-buttons">
                <a
                  href="mailto:support@blackkiteai.com?subject=Yearly%20Subscription%20Inquiry"
                  className="btn-contact-sales email"
                >
                  <span role="img" aria-label="email">📧</span> Email Sales
                </a>
                <a
                  href="https://wa.me/917259730473?text=Hi%2C%20I%27m%20interested%20in%20the%20Yearly%20Subscription%20plan%20(Unlimited%20Credits%20%E2%82%B916%2C000%2Fyear)"
                  className="btn-contact-sales whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span role="img" aria-label="whatsapp">💬</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Lifetime Access Card */}
        {!loading && !error && (
          <div className="pricing-card lifetime-access">
            <div className="pricing-card-icon">
              {Array.from({ length: 20 }).map((_, i) => (
                <span key={i} className="icon-dot"></span>
              ))}
            </div>

            <h2 className="pricing-card-title">Lifetime Access</h2>

            <div className="plan-icon" aria-hidden="true">💎</div>

            <div className="pricing-card-price">
              <span className="price-amount">
                {displayCurrency === 'USD'
                  ? formatPrice(Number((35000 / usdInrRate).toFixed(2)), 'USD')
                  : '₹35,000'}
              </span>
              <span className="gst-label">+ 18% GST</span>
              <span className="subscription-period">One-time payment</span>
            </div>

            <div className="pricing-card-credits">
              <strong>
                <span className="credits-bonus">Unlimited</span>
                <span className="credits-text"> Credits</span>
              </strong>
              <div className="credits-minutes">Unlimited minutes</div>
              <div className="plan-description">Lifetime Access plan</div>
            </div>

            <div className="refund-policy-notice">
              <p>
                Please read our <Link to="/refund-policy" className="refund-policy-link">Refund Policy</Link> before purchasing.
              </p>
            </div>

            <div className="subscription-contact">
              <p className="subscription-contact-title">Connect with our Sales Team</p>
              <div className="subscription-contact-buttons">
                <a
                  href="mailto:support@blackkiteai.com?subject=Lifetime%20Access%20Inquiry"
                  className="btn-contact-sales email"
                >
                  <span role="img" aria-label="email">📧</span> Email Sales
                </a>
                <a
                  href="https://wa.me/917259730473?text=Hi%2C%20I%27m%20interested%20in%20the%20Lifetime%20Access%20plan%20(Unlimited%20Credits%20%E2%82%B935%2C000)"
                  className="btn-contact-sales whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span role="img" aria-label="whatsapp">💬</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
